import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { User, Calendar, Activity, AlertCircle, TrendingUp, Clock } from 'lucide-react';

export const UserDetailsModal = ({ isOpen, onClose, userDetails, loading }) => {
  if (!userDetails && !loading) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString('id-ID');
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge variant="success" className="bg-green-500 text-white" data-testid="status-badge-active">
        Active
      </Badge>
    ) : (
      <Badge variant="destructive" data-testid="status-badge-inactive">
        Inactive
      </Badge>
    );
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-purple-500 text-white',
      user: 'bg-blue-500 text-white',
      developer: 'bg-orange-500 text-white'
    };
    return (
      <Badge className={colors[role] || colors.user} data-testid="role-badge">
        {role?.toUpperCase() || 'USER'}
      </Badge>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full" data-testid="user-details-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <User className="h-6 w-6" />
            User Details
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12" data-testid="loading-indicator">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Name</label>
                    <p className="mt-1 text-base font-semibold" data-testid="user-name">{userDetails.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Email</label>
                    <p className="mt-1 text-base" data-testid="user-email">{userDetails.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Status</label>
                    <div className="mt-1">{getStatusBadge(userDetails.is_active)}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Role</label>
                    <div className="mt-1">{getRoleBadge(userDetails.role)}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">API Key</label>
                    <p className="mt-1 font-mono text-sm bg-gray-100 p-2 rounded break-all" data-testid="user-api-key">
                      {userDetails.api_key}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Created At</label>
                    <p className="mt-1 text-sm flex items-center gap-2" data-testid="user-created-at">
                      <Calendar className="h-4 w-4" />
                      {formatDate(userDetails.created_at)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Last Login</label>
                    <p className="mt-1 text-sm flex items-center gap-2" data-testid="user-last-login">
                      <Clock className="h-4 w-4" />
                      {formatDate(userDetails.last_login)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Last Request</label>
                    <p className="mt-1 text-sm" data-testid="user-last-request">
                      {formatDate(userDetails.last_request_at)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Statistics */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Usage Statistics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Total Requests</p>
                    <p className="text-2xl font-bold text-blue-600" data-testid="total-requests">
                      {userDetails.total_requests?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Total Errors</p>
                    <p className="text-2xl font-bold text-red-600" data-testid="total-errors">
                      {userDetails.total_errors?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Today</p>
                    <p className="text-2xl font-bold text-green-600" data-testid="requests-today">
                      {userDetails.requests_today?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">This Week</p>
                    <p className="text-2xl font-bold text-purple-600" data-testid="requests-week">
                      {userDetails.requests_this_week?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">This Month</p>
                    <p className="text-2xl font-bold text-orange-600" data-testid="requests-month">
                      {userDetails.requests_this_month?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className="bg-indigo-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Error Rate</p>
                    <p className="text-2xl font-bold text-indigo-600" data-testid="error-rate">
                      {userDetails.total_requests > 0
                        ? ((userDetails.total_errors / userDetails.total_requests) * 100).toFixed(2)
                        : 0}%
                    </p>
                  </div>
                  <div className="bg-teal-50 p-4 rounded-lg col-span-2">
                    <p className="text-sm text-gray-600">Avg Response Time</p>
                    <p className="text-2xl font-bold text-teal-600" data-testid="avg-response-time">
                      {userDetails.avg_response_time?.toFixed(2) || 0} ms
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Most Used Endpoints */}
            {userDetails.most_used_endpoints && userDetails.most_used_endpoints.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Most Used Endpoints
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3" data-testid="most-used-endpoints">
                    {userDetails.most_used_endpoints.map((endpoint, index) => (
                      <div key={index} className="flex items-center justify-between border-b pb-2">
                        <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                          {endpoint.endpoint}
                        </code>
                        <Badge variant="secondary">
                          {endpoint.count.toLocaleString()} requests
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Warning if errors are high */}
            {userDetails.total_requests > 0 && 
             (userDetails.total_errors / userDetails.total_requests) > 0.1 && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 text-red-800">
                    <AlertCircle className="h-5 w-5" />
                    <div>
                      <p className="font-semibold">High Error Rate Detected</p>
                      <p className="text-sm">
                        This user has an error rate above 10%. Consider investigating their API usage.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
